import { useState } from 'react';

export default function NewsletterSection() {
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formElement = e.target as HTMLFormElement;
      const formDataToSend = new FormData(formElement);
      
      const response = await fetch('https://readdy.ai/api/form/d4fjftse56aa2r6jvc80', {
        method: 'POST',
        body: new URLSearchParams(formDataToSend as any),
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
      });

      if (response.ok) {
        setSubmitStatus('success');
        setEmail('');
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section className="py-20 relative overflow-hidden" style={{ background: '#1a1a2e' }}>
      {/* Animated background particles */}
      <div className="absolute inset-0">
        <div className="absolute top-20 left-10 w-32 h-32 bg-amber-400/20 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute top-40 right-20 w-40 h-40 bg-yellow-400/20 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }}></div>
        <div className="absolute bottom-20 left-1/3 w-36 h-36 bg-amber-500/20 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '2s' }}></div>
      </div>
      
      <div className="relative z-10 max-w-4xl mx-auto px-6 lg:px-8 text-center">
        <div className="bg-gradient-to-br from-slate-800/90 to-slate-900/90 backdrop-blur-xl rounded-3xl p-12 border-2 border-amber-400/40 shadow-2xl">
          <div className="mb-8">
            <div className="inline-block p-4 rounded-full bg-gradient-to-r from-amber-500 to-yellow-500 mb-6 shadow-lg">
              <i className="ri-mail-line text-4xl text-white"></i>
            </div>
          </div>

          <h2 className="text-4xl lg:text-5xl font-bold mb-6 text-white" style={{ fontFamily: '"Playfair Display", serif' }}>
            Join Our <span className="bg-gradient-to-r from-amber-400 to-yellow-300 bg-clip-text text-transparent">Community</span>
          </h2>
          
          <p className="text-xl mb-8 leading-relaxed text-gray-200">
            Subscribe to receive exclusive insights, transformational content, and updates on upcoming events from The CROWN Abundance Movement.
          </p>

          <form id="newsletter-form" data-readdy-form onSubmit={handleSubmit} className="max-w-md mx-auto">
            <div className="flex flex-col sm:flex-row gap-4 mb-6">
              <input
                type="email"
                name="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your email"
                required
                className="flex-1 px-6 py-4 rounded-xl bg-white text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-amber-400 focus:border-transparent transition-all duration-300 shadow-lg text-sm"
              />
              <button
                type="submit"
                disabled={isSubmitting}
                className="bg-gradient-to-r from-amber-500 to-yellow-400 hover:from-amber-600 hover:to-yellow-500 text-white px-8 py-4 rounded-xl font-semibold transition-all duration-300 cursor-pointer whitespace-nowrap shadow-lg hover:shadow-xl transform hover:-translate-y-1 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
              >
                {isSubmitting ? (
                  <>
                    <i className="ri-loader-4-line animate-spin mr-2"></i>
                    Subscribing...
                  </>
                ) : (
                  <>
                    <i className="ri-send-plane-fill mr-2"></i>
                    Subscribe
                  </>
                )}
              </button>
            </div>

            {submitStatus === 'success' && (
              <div className="bg-emerald-500/20 border-2 border-emerald-400 rounded-xl p-4 flex items-center justify-center">
                <i className="ri-checkbox-circle-line text-xl text-emerald-300 mr-2"></i>
                <p className="text-emerald-300 font-medium">
                  Successfully subscribed! Check your inbox.
                </p>
              </div>
            )}

            {submitStatus === 'error' && (
              <div className="bg-red-500/20 border-2 border-red-400 rounded-xl p-4 flex items-center justify-center">
                <i className="ri-error-warning-line text-xl text-red-300 mr-2"></i>
                <p className="text-red-300 font-medium">
                  Something went wrong. Please try again.
                </p>
              </div>
            )}
          </form>

          <div className="grid grid-cols-3 gap-8 mt-12 pt-8 border-t-2 border-amber-400/30">
            <div>
              <div className="text-3xl font-bold mb-2">
                <i className="ri-global-line bg-gradient-to-r from-amber-400 to-yellow-300 bg-clip-text text-transparent"></i>
              </div>
              <div className="text-sm text-gray-300">Transformational Insights</div>
            </div>
            <div>
              <div className="text-3xl font-bold mb-2">
                <i className="ri-calendar-line bg-gradient-to-r from-amber-400 to-yellow-300 bg-clip-text text-transparent"></i>
              </div>
              <div className="text-sm text-gray-300">Weekly Updates</div>
            </div>
            <div>
              <div className="text-3xl font-bold mb-2">
                <i className="ri-vip-crown-line bg-gradient-to-r from-amber-400 to-yellow-300 bg-clip-text text-transparent"></i>
              </div>
              <div className="text-sm text-gray-300">Exclusive Content</div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
